import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
// useSearchParams só é exportado pelo react-router-dom nesta versão (v6).
import { useSearchParams } from "react-router-dom";

import AuthRoutes from "Routes/Components/Auth";

import { HTTPErrorCallback } from "Helpers/Error";
import { setPostLoginRedirect } from "Helpers/PostLoginRedirect";

import UseAuth from "Hooks/UseAuth";
import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import KpiComponent from "Components/Common/KpiComponent";

import ProjectsCalendar from "./Components/Calendar";
import KpiDetails from "./Components/KpiDetails";
import AllProjects from "./Components/List";
import TabSelector from "./Components/TabSelector";
import * as S from "./styles";

const ListProjects = () => {
  const { authorized, parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { setLoading } = UseGlobalLoading();
  const navigate = useNavigate();
  const { uploadFile } = UseFile();

  const inputRef = useRef(null);

  const [currentTab, setCurrentTab] = useState("calendar");
  const [projects, setProjects] = useState([]);
  // Aba "Projetos Aportados" do proponente (18/08/2026): lista própria, porque o
  // recorte é outro (só projetos com vínculo em "Aportado").
  const [contributedProjects, setContributedProjects] = useState([]);
  const [sponsorSelectives, setSponsorSelectives] = useState([]);
  const [dashboard, setDashboard] = useState({});

  const [bannerImg, setBannerImg] = useState(null);

  const [showCalendar, setShowCalendar] = useState(true);

  const [selectedFilter, setSelectedFilter] = useState(null);

  // Filtros da aba da carteira (patrocinador): { title, territorio,
  // category_ids }. Vão para o backend; os KPIs NÃO acompanham o filtro.
  const [projectFilters, setProjectFilters] = useState({});

  // Detalhe do KPI clicado (título, colunas e linhas vêm do backend).
  const [kpiDetails, setKpiDetails] = useState(null);

  const [selected, setSelected] = useState(null);

  // Deep link para abrir um projeto direto: /list-projects?project_id=X
  // (a RESPOSTA do e-mail de validação não passa mais por aqui: tem página
  // pública própria, /validacao-projeto, que dispensa login).
  const [searchParams] = useSearchParams();
  const deepProjectId = searchParams.get("project_id");
  // Aba de destino do deep link (lista fechada). O link do Calendário manda
  // "list": o calendário só mostra projetos aportados, que vivem em "Meus
  // Projetos". Sem o parâmetro, o comportamento de antes é preservado.
  const deepTab = ["calendar", "list", "selectives", "contributed"].includes(
    searchParams.get("tab"),
  )
    ? searchParams.get("tab")
    : null;

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  useEffect(() => {
    if (!authorized) {
      // Guarda o destino (com os query params) para retomar depois do login —
      // o login comum é um modal na home, não uma rota.
      setPostLoginRedirect(window.location.pathname + window.location.search);
      navigate(AuthRoutes.Dashboard.path);
      return;
    }

    if (parsedJWT?.user?.banner_src) {
      setBannerImg(parsedJWT?.user?.banner_src);
    }
  }, [authorized, parsedJWT]);

  // Abre direto o projeto indicado pelo link. O e-mail de validação vai para o
  // PROPONENTE, então o destino é a aba de projetos dele ("Meus Projetos") — a
  // aba "Seletivas" é exclusiva do patrocinador.
  //
  // Os query params são mantidos de propósito: o AuthProvider renderiza
  // `{!loading && children}` e o refreshProfile do mount desmonta/remonta esta
  // página — se limpássemos a URL na primeira montagem, a intenção do link se
  // perderia na segunda. Reprocessar é inofensivo porque este deep link apenas
  // ABRE o projeto; a resposta da validação nunca passou por aqui (tem página
  // pública própria, /validacao-projeto).
  //
  // ⚠️ Desde 18/08/2026 `update-project-validation` NÃO é mais idempotente:
  // responder duas vezes é recusado de propósito.
  useEffect(() => {
    if (!authorized || !deepProjectId) return;

    setCurrentTab(deepTab || (is_sponsor ? "selectives" : "list"));
    setSelected({ id: Number(deepProjectId) });
    // eslint-disable-next-line
  }, [authorized, deepProjectId, deepTab]);

  useEffect(() => {
    getDashboard();
    // eslint-disable-next-line
  }, [selected]);

  // Uma chamada só para os dois filtros (interesse e os da carteira): efeitos
  // separados disparavam duas requisições a cada montagem.
  useEffect(() => {
    getProjects(selectedFilter?.value, projectFilters);
    if (!is_sponsor) getContributedProjects();
    // eslint-disable-next-line
  }, [selected, selectedFilter, projectFilters]);

  // Projetos aportados do proponente. Chamada separada porque o recorte
  // (`contributed_only`) é outro e a aba tem lista própria.
  const getContributedProjects = async () => {
    try {
      const response = await api.post("/projects/list-active-projects", {
        contributed_only: true,
      });

      setContributedProjects(response.data?.projects ?? []);
    } catch (error) {
      HTTPErrorCallback(error);
    }
  };

  const getProjects = async (filter = null, projectFilters = {}) => {
    setLoading(true);

    const filters = {};

    if (filter) filters.sponsor_filter = filter;

    // Só as chaves preenchidas viajam: campo vazio não filtra.
    const activeFilters = Object.entries(projectFilters).reduce(
      (acc, [key, value]) => {
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && !value.length);

        if (!isEmpty) acc[key] = value;

        return acc;
      },
      {},
    );

    if (Object.keys(activeFilters).length) filters.filters = activeFilters;

    try {
      const response = await api.post(
        "/projects/list-active-projects",
        filters,
      );

      // Para o patrocinador, `projects` é a carteira de projetos APORTADOS (aba
      // "Meus Projetos"); a aba "Seletivas" vem de `sponsor_selectives`, agrupada.
      setProjects(response.data?.projects ?? []);
      setSponsorSelectives(response.data?.sponsor_selectives ?? []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const getDashboard = async () => {
    setLoading(true);

    try {
      const response = await api.get("/projects/dashboard");

      setDashboard(response.data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  // Trocar de aba fecha o detalhe do projeto que estiver aberto (20/08/2026).
  // Sem isto, clicar numa aba com um projeto aberto não tinha efeito visível: o
  // detalhe ocupa a área toda e o `currentTab` mudava por baixo dele, então o
  // usuário clicava e nada acontecia.
  const changeTab = (tab) => {
    setSelected(null);
    setCurrentTab(tab);
  };

  // O backend diz quais cartões têm ação e qual é ela (chave reservada
  // `_actions`): trocar de aba ou abrir o modal com a lista discriminada.
  const kpiActions = dashboard?._actions || {};

  const handleKpiClick = async (title) => {
    const action = kpiActions[title];

    if (!action) return;

    if (action.type === "tab") {
      changeTab(action.tab);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/projects/dashboard-details", {
        kpi: action.kpi,
      });

      setKpiDetails(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  const updateBannerSrc = async (bannerImg) => {
    setLoading(true);

    try {
      const image_location = await uploadFile(bannerImg);

      await api.post("/users/update-banner-img", {
        banner_src: image_location,
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  useEffect(() => {
    if (!showCalendar) setCurrentTab("list");
  }, [showCalendar]);

  const getCurrentTab = () => {
    switch (currentTab) {
      case "calendar":
        return <ProjectsCalendar setShowCalendar={setShowCalendar} />;
      case "list":
        return (
          <AllProjects
            key={currentTab}
            projects={projects}
            selected={selected}
            setSelected={setSelected}
            projectFilters={projectFilters}
            setProjectFilters={setProjectFilters}
          />
        );
      case "contributed":
        return (
          <AllProjects
            key={currentTab}
            projects={contributedProjects}
            selected={selected}
            setSelected={setSelected}
            hideCreateButton
            emptyMessage="Nenhum projeto aportado ainda."
          />
        );
      case "selectives":
        return (
          <AllProjects
            key={currentTab}
            selectives={sponsorSelectives}
            isSelective
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selected={selected}
            setSelected={setSelected}
          />
        );
      default:
        return <ProjectsCalendar />;
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      setBannerImg(e.target.result);
    };

    reader.readAsDataURL(file);

    updateBannerSrc(file);
  };

  return (
    <>
      <S.BannerArea onClick={() => inputRef.current.click()} hasBg={bannerImg}>
        {bannerImg ? (
          <img src={bannerImg} alt="banner" />
        ) : (
          <>
            <h2>ESCOLHER IMAGEM DE CAPA</h2>
            <span>
              Resolução recomendada: <strong>1920x350</strong>
            </span>
          </>
        )}

        <input type="file" onChange={handleFileUpload} ref={inputRef} />
      </S.BannerArea>

      {/* {!is_sponsor ? (
        <S.Header>
        <div></div>
        <Button
        text="Novo Projeto"
        type="button"
        onClick={() => navigate(AuthRoutes.CreateProject.path)}
        />
        </S.Header>
        ) : null} */}

      <S.Wrapper>
        <S.Kpis>
          {Object.keys(dashboard)
            // Chaves com "_" não são cartão (hoje, só `_actions`).
            .filter((key) => !key.startsWith("_"))
            .map((key) => (
              <KpiComponent
                key={key}
                title={key}
                value={dashboard[key]}
                onClick={
                  kpiActions[key] ? () => handleKpiClick(key) : undefined
                }
              />
            ))}
        </S.Kpis>

        <S.Header>
          <S.Title>PROJETOS</S.Title>
          <S.Subtitle>
            Edite projetos e confira as diversas datas relacionadas.
          </S.Subtitle>
        </S.Header>

        <S.Main>
          <TabSelector
            showCalendar={showCalendar}
            currentTab={currentTab}
            setCurrentTab={changeTab}
            isSponsor={is_sponsor}
          />

          {getCurrentTab()}
        </S.Main>
      </S.Wrapper>

      <KpiDetails details={kpiDetails} onClose={() => setKpiDetails(null)} />
    </>
  );
};

export default ListProjects;
